import type {FeGridSettings, GetDataQueryResponse} from '../types/Api';
import type {EagColumnState} from '../types/EagColumnState';
import type {Filters} from '../types/Filters';

/**
 * Snapshot of the grid state captured at the time of the last successful fetch.
 * Used by `useServerData` to detect what changed between fetches — enabling
 * optimizations like skipping the /count request on pagination-only changes.
 */
export type FetchStateSnapshot = {
  filterValues: Filters | undefined;
  dataQuery: GetDataQueryResponse | null;
  columnStates: EagColumnState[] | null;
  gridSettings: FeGridSettings | undefined;
  presetJson: string | null;
  comparisonJson: string | null;
  queryId: string | null;
};

/** Initial snapshot before any fetch has occurred. All fields are null/undefined. */
export const EMPTY_SNAPSHOT: FetchStateSnapshot = {
  filterValues: undefined,
  dataQuery: null,
  columnStates: null,
  gridSettings: undefined,
  presetJson: null,
  comparisonJson: null,
  queryId: null,
};

type StoreState = Pick<FetchStateSnapshot, 'filterValues' | 'dataQuery' | 'gridSettings'> & {
  columnStates: EagColumnState[]; // non-nullable — store always has column states
};

/**
 * Checks whether this fetchPage call is a pagination-only change (only offset differs).
 * Uses reference equality on raw Zustand store values — cheap and safe because Zustand
 * preserves object identity when state hasn't been mutated.
 */
export function isPaginationOnly(snapshot: FetchStateSnapshot, state: StoreState): boolean {
  return (
    snapshot.queryId !== null &&
    state.filterValues === snapshot.filterValues &&
    state.dataQuery === snapshot.dataQuery &&
    state.columnStates === snapshot.columnStates &&
    state.gridSettings === snapshot.gridSettings
  );
}

/**
 * Creates a new snapshot by copying current store state and selectively updating
 * derived fields. Omitted derived fields are preserved from the previous snapshot;
 * explicitly passing `null` resets them.
 */
export function updateSnapshot(
  snapshot: FetchStateSnapshot,
  state: StoreState,
  derived: {presetJson?: string | null; comparisonJson?: string | null; queryId?: string | null}
): FetchStateSnapshot {
  return {
    filterValues: state.filterValues,
    dataQuery: state.dataQuery,
    columnStates: state.columnStates,
    gridSettings: state.gridSettings,
    presetJson: derived.presetJson !== undefined ? derived.presetJson : snapshot.presetJson,
    comparisonJson:
      derived.comparisonJson !== undefined ? derived.comparisonJson : snapshot.comparisonJson,
    queryId: derived.queryId !== undefined ? derived.queryId : snapshot.queryId,
  };
}
